(() => {
  const SECTION_SELECTOR =
    '[data-wambolt-statement]';

  const TARGET_SELECTOR =
    '[data-highlight-text]';

  const READY_ATTRIBUTE =
    'data-wambolt-statement-ready';

  const SPLIT_CLASS =
    'is-highlight-split';

  const instances =
    new Map();


  /* =========================================
     DEPENDENCIES
     ========================================= */

  function getDependencies() {
    const gsap =
      window.gsap;

    const ScrollTrigger =
      window.ScrollTrigger;

    const SplitText =
      window.SplitText;


    if (
      !gsap ||
      !ScrollTrigger ||
      !SplitText
    ) {
      return null;
    }


    gsap.registerPlugin(
      ScrollTrigger,
      SplitText
    );


    return {
      gsap,
      ScrollTrigger,
      SplitText
    };
  }


  /* =========================================
     SCROLLER
     ========================================= */

  function getScroller() {
    const scrollElement =
      window.wamboltScrollElement;


    if (
      !scrollElement ||
      scrollElement === window ||
      scrollElement === document ||
      scrollElement === document.documentElement ||
      scrollElement === document.body
    ) {
      return null;
    }


    return scrollElement;
  }


  /* =========================================
     REDUCED MOTION
     ========================================= */

  function prefersReducedMotion() {
    return window.matchMedia(
      '(prefers-reduced-motion: reduce)'
    ).matches;
  }


  /* =========================================
     GET SECTIONS
     ========================================= */

  function getSections(scope = document) {
    const sections =
      [];


    if (
      scope instanceof Element &&
      scope.matches(SECTION_SELECTOR)
    ) {
      sections.push(
        scope
      );
    }


    if (
      scope &&
      typeof scope.querySelectorAll ===
        'function'
    ) {
      sections.push(
        ...scope.querySelectorAll(
          SECTION_SELECTOR
        )
      );
    }


    return [
      ...new Set(sections)
    ];
  }


  /* =========================================
     DESTROY SECTION
     ========================================= */

  function destroySection(section) {
    const instance =
      instances.get(section);


    if (instance) {

      if (instance.context) {
        instance.context.revert();
      }


      if (instance.splits) {

        instance.splits.forEach(
          (split) => {

            if (
              split &&
              typeof split.revert ===
                'function'
            ) {
              split.revert();
            }

          }
        );

      }


      instances.delete(
        section
      );
    }


    section
      .querySelectorAll(
        TARGET_SELECTOR
      )
      .forEach((target) => {

        target.classList.remove(
          SPLIT_CLASS
        );

        target.style.removeProperty(
          'opacity'
        );

        target.style.removeProperty(
          'visibility'
        );

      });


    section.removeAttribute(
      READY_ATTRIBUTE
    );
  }


  /* =========================================
     INITIALIZE SECTION
     ========================================= */

  function initSection(section) {

    if (
      !(section instanceof Element) ||
      section.hasAttribute(
        READY_ATTRIBUTE
      )
    ) {
      return;
    }


    const dependencies =
      getDependencies();


    if (!dependencies) {
      return;
    }


    const {
      gsap,
      SplitText
    } = dependencies;


    const targets =
      Array.from(
        section.querySelectorAll(
          TARGET_SELECTOR
        )
      );


    if (!targets.length) {

      section.setAttribute(
        READY_ATTRIBUTE,
        ''
      );

      return;
    }


    /* =========================================
       REDUCED MOTION
       ========================================= */

    if (prefersReducedMotion()) {

      gsap.set(
        targets,
        {
          opacity:
            1,

          visibility:
            'visible'
        }
      );


      section.setAttribute(
        READY_ATTRIBUTE,
        ''
      );


      return;
    }


    const scroller =
      getScroller();


    const splits =
      [];


    /* =========================================
       GSAP CONTEXT
       ========================================= */

    const context =
      gsap.context(() => {

        targets.forEach((heading) => {

          const scrollStart =
            heading.getAttribute(
              'data-highlight-scroll-start'
            ) ||
            'top 90%';


          const scrollEnd =
            heading.getAttribute(
              'data-highlight-scroll-end'
            ) ||
            'center 40%';


          const fadedAttribute =
            heading.getAttribute(
              'data-highlight-fade'
            );


          const staggerAttribute =
            heading.getAttribute(
              'data-highlight-stagger'
            );


          const fadedValue =
            fadedAttribute !== null
              ? Number(fadedAttribute)
              : 0.2;


          const staggerValue =
            staggerAttribute !== null
              ? Number(staggerAttribute)
              : 0.1;


          const split =
            new SplitText(
              heading,
              {
                type:
                  'words, chars',

                autoSplit:
                  true,

                wordsClass:
                  'word',

                charsClass:
                  'char',


                /* =================================
                   SPLIT / RESPLIT
                   ================================= */

                onSplit(self) {

                  /*
                   * CRITICAL INITIALIZATION ORDER:
                   *
                   * 1. Original unsplit heading is
                   *    already at 20% via CSS.
                   *
                   * 2. SplitText creates characters.
                   *
                   * 3. Every character is immediately
                   *    initialized to the same faded
                   *    value.
                   *
                   * 4. Only AFTER that do we restore
                   *    the parent heading to opacity 1.
                   *
                   * There is therefore never a frame
                   * where fresh characters are allowed
                   * to display at full opacity.
                   */

                  gsap.set(
                    self.chars,
                    {
                      autoAlpha:
                        fadedValue
                    }
                  );


                  heading.classList.add(
                    SPLIT_CLASS
                  );


                  const triggerSettings = {
                    trigger:
                      heading,

                    start:
                      scrollStart,

                    end:
                      scrollEnd,

                    scrub:
                      true,

                    invalidateOnRefresh:
                      true
                  };


                  if (scroller) {
                    triggerSettings.scroller =
                      scroller;
                  }


                  /*
                   * Characters already have their
                   * correct initial state, so this
                   * animation only has one job:
                   *
                   * progressively move them from
                   * faded -> fully visible.
                   */

                  const animation =
                    gsap.to(
                      self.chars,
                      {
                        autoAlpha:
                          1,

                        stagger:
                          staggerValue,

                        ease:
                          'linear',

                        scrollTrigger:
                          triggerSettings
                      }
                    );


                  return animation;
                }
              }
            );


          splits.push(
            split
          );

        });

      }, section);


    instances.set(
      section,
      {
        context,
        splits
      }
    );


    section.setAttribute(
      READY_ATTRIBUTE,
      ''
    );
  }


  /* =========================================
     INITIALIZE SCOPE
     ========================================= */

  function initScope(scope = document) {

    getSections(scope).forEach(
      initSection
    );

  }


  /* =========================================
     DESTROY SCOPE
     ========================================= */

  function destroyScope(scope = document) {

    getSections(scope).forEach(
      destroySection
    );

  }


  /* =========================================
     REFRESH
     ========================================= */

  function refreshScrollTrigger() {

    requestAnimationFrame(() => {

      requestAnimationFrame(() => {

        window.ScrollTrigger?.refresh();

      });

    });

  }


  /* =========================================
     INITIAL LOAD
     ========================================= */

  function start() {

    initScope(
      document
    );


    refreshScrollTrigger();

  }


  function startWhenReady() {

    /*
     * CSS is already showing the correct
     * low-opacity initial state while fonts
     * load, so waiting here no longer creates
     * a visual flash.
     */

    if (
      document.fonts &&
      document.fonts.ready
    ) {

      document.fonts.ready
        .then(start)
        .catch(start);

      return;
    }


    start();
  }


  if (
    document.readyState ===
    'loading'
  ) {

    document.addEventListener(
      'DOMContentLoaded',
      startWhenReady,
      {
        once:
          true
      }
    );

  } else {

    startWhenReady();

  }


  /* =========================================
     BARBA — BEFORE LEAVE
     ========================================= */

  window.addEventListener(
    'wambolt:page:before-leave',
    (event) => {

      const container =
        event.detail?.container;


      if (container) {

        destroyScope(
          container
        );

      }

    }
  );


  /* =========================================
     BARBA — ENTER
     ========================================= */

  window.addEventListener(
    'wambolt:page:enter',
    (event) => {

      const container =
        event.detail?.container ||
        document;


      const init =
        () => {

          initScope(
            container
          );


          refreshScrollTrigger();

        };


      if (
        document.fonts &&
        document.fonts.ready
      ) {

        document.fonts.ready
          .then(init)
          .catch(init);

      } else {

        init();

      }

    }
  );


  /* =========================================
     SHOPIFY SECTION LOAD
     ========================================= */

  document.addEventListener(
    'shopify:section:load',
    (event) => {

      const init =
        () => {

          initScope(
            event.target
          );


          refreshScrollTrigger();

        };


      if (
        document.fonts &&
        document.fonts.ready
      ) {

        document.fonts.ready
          .then(init)
          .catch(init);

      } else {

        init();

      }

    }
  );


  /* =========================================
     SHOPIFY SECTION UNLOAD
     ========================================= */

  document.addEventListener(
    'shopify:section:unload',
    (event) => {

      destroyScope(
        event.target
      );

    }
  );
})();